<script setup lang="ts">
import type {
	PropertyTypeHighlightClickPayload,
	PropertyTypeSelectProps
} from '.';
import type { Property } from '~/core/entities/property';

import { normalizeString } from '@/utils/index';
import {
	PropertyTypeEnum,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';

import { DefaultFilterSelect } from '../DefaultFilterSelect/';
import messages from './PropertyTypeSelect.locale.json';

const strings = messages.pt;

const props = defineProps<PropertyTypeSelectProps>();

const emit = defineEmits<{
	'click-highlight-item': [payload: PropertyTypeHighlightClickPayload];
}>();

const selectedPropertyTypes = defineModel<PropertyTypeEnum[]>({
	required: true,
	default: () => []
});

interface DefaultFilterSelectExposed {
	focusFirstInputWhenReady: () => void;
}

const selectRef = useTemplateRef<DefaultFilterSelectExposed>('selectRef');

defineExpose({
	focusFirstInputWhenReady: () => selectRef.value?.focusFirstInputWhenReady()
});

const modelValue = ref<{ value: PropertyTypeEnum; label: string }[]>([]);

// Tipologias comerciais do CRM que não têm inventário no portal.
const excludedPropertyTypes = [
	PropertyTypeEnum.COMMERCIAL,
	PropertyTypeEnum.COMMERCIAL_BUILDING,
	PropertyTypeEnum.COMMERCIAL_POINT,
	PropertyTypeEnum.COMMERCIAL_SET,
	PropertyTypeEnum.INDUSTRIAL_WAREHOUSE
];

/** Tipos em destaque padrão conforme Figma (quando não há dados do feed/listing) */
const DEFAULT_HIGHLIGHTED_TYPES: PropertyTypeEnum[] = [
	PropertyTypeEnum.PENTHOUSE,
	PropertyTypeEnum.HOUSE,
	PropertyTypeEnum.DUPLEX,
	PropertyTypeEnum.TRIPLEX,
	PropertyTypeEnum.APARTMENT
];

const items = computed<{ value: PropertyTypeEnum; label: string }[]>(() => {
	const types = Object.keys(propertyTypesTranslations) as PropertyTypeEnum[];

	return types
		.map(typeKey => ({
			value: typeKey,
			label: propertyTypesTranslations[typeKey]
		}))
		.filter(({ value }) => !excludedPropertyTypes.includes(value))
		.sort((typeA, typeB) => {
			const typeATranslation = propertyTypesTranslations[typeA.value];
			const typeBTranslation = propertyTypesTranslations[typeB.value];

			return typeATranslation.localeCompare(typeBTranslation);
		});
});

function getItemLabel({
	label
}: {
	value: PropertyTypeEnum;
	label: string;
}): string {
	return label;
}

function filter(
	items: {
		value: PropertyTypeEnum;
		label: string;
	}[],
	term: string
): {
	value: PropertyTypeEnum;
	label: string;
}[] {
	const normalizedTerm = normalizeString(term);

	return items.filter(item => {
		const normalizedItem = normalizeString(item.label);

		return normalizedItem.includes(normalizedTerm);
	});
}

const { cookie: favoritesCookie } = useFavoritesCookie();

/**
 * Dynamic state key that includes favorites cookie for cache isolation
 */
const stateKey = computed(() => {
	const cookieValue = favoritesCookie.value || 'empty';
	return `_properties-listing-${cookieValue}`;
});

const propertiesState = useState<{
	_properties: Property[];
	_cachedProperties: Property[];
}>(stateKey.value, () => ({
	_properties: [],
	_cachedProperties: []
}));

const effectiveHighlightedItems = computed(() => {
	const excluded = selectedPropertyTypes.value;

	if (props.highlightedItems?.length) {
		return props.highlightedItems.filter(
			({ value }) => !excluded.includes(value)
		);
	}
	if (highlightedPropertyTypes.value.length) {
		return highlightedPropertyTypes.value;
	}
	// Fallback: tipos em destaque padrão conforme Figma quando não há dados do feed/listing
	return DEFAULT_HIGHLIGHTED_TYPES.filter(type => !excluded.includes(type)).map(
		value => ({
			value,
			label: propertyTypesTranslations[value]
		})
	);
});

const highlightedPropertyTypes = computed(() => {
	// Check if propertiesState exists and has properties
	if (!propertiesState.value || !propertiesState.value._properties) {
		return [];
	}

	const types = Array.from(
		propertiesState.value._properties
			.reduce((types, { propertyType }) => {
				const type = types.get(propertyType.identifier) || 0;

				types.set(propertyType.identifier, type + 1);

				return types;
			}, new Map())
			.entries()
	);

	if (types.length < 3) return [];

	return types
		.sort(([, a], [, b]) => b - a)
		.map(([type]) => ({
			value: type,
			label: propertyTypesTranslations[type as PropertyTypeEnum]
		}))
		.filter(({ value }) => !selectedPropertyTypes.value.includes(value))
		.splice(0, 5);
});

function updateModelValue(
	newValues: { value: PropertyTypeEnum; label: string }[]
) {
	modelValue.value = newValues.map(toRaw);
	selectedPropertyTypes.value = modelValue.value.map(({ value }) => value);
}

watch(
	() => selectedPropertyTypes.value,
	() => {
		const newValue = selectedPropertyTypes.value.map(value => ({
			value,
			label: propertyTypesTranslations[value]
		}));

		if (newValue === modelValue.value) return;

		modelValue.value = newValue;
	},
	{ immediate: true, deep: true }
);
</script>

<template>
	<DefaultFilterSelect
		ref="selectRef"
		:model-value="modelValue"
		:title="strings.title"
		:items="items"
		:highlighted-items="effectiveHighlightedItems"
		:get-item-label="getItemLabel"
		:filter-function="filter"
		:separator-variant="props.separatorVariant"
		:variant="props.variant"
		@update:model-value="updateModelValue"
		@click-highlight-item="emit('click-highlight-item', $event)"
	/>
</template>
